<template>
  <div class="form-container card shadow-sm p-3">
    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ transferencia.id ? 'Editar Transferência entre Contas' : 'Nova Transferência entre Contas' }}
      </h4>
      <button type="button" class="btn btn-secondary" @click="voltar">Voltar</button>
    </div>

    <div class="spacer"></div>

    <BaseForm
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarTransferencia"
      @cancel="voltar"
    >
      <div class="form-section">
        <h5 class="section-title">Dados da Transferência</h5>

        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label>Conta Origem <span class="required">*</span></label>
              <select
                v-model="transferencia.conta_origem_id"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.conta_origem_id }"
              >
                <option value="">Selecione</option>
                <option v-for="conta in contas" :key="conta.id" :value="conta.id">
                  {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
                </option>
              </select>
              <span v-if="formSubmitted && errors.conta_origem_id" class="error-message">
                {{ errors.conta_origem_id }}
              </span>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group">
              <label>Conta Destino <span class="required">*</span></label>
              <select
                v-model="transferencia.conta_destino_id"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.conta_destino_id }"
              >
                <option value="">Selecione</option>
                <option v-for="conta in contas" :key="conta.id" :value="conta.id">
                  {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
                </option>
              </select>
              <span v-if="formSubmitted && errors.conta_destino_id" class="error-message">
                {{ errors.conta_destino_id }}
              </span>
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group">
              <label>Data <span class="required">*</span></label>
              <input
                v-model="transferencia.data_transferencia"
                type="date"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.data_transferencia }"
              />
              <span v-if="formSubmitted && errors.data_transferencia" class="error-message">
                {{ errors.data_transferencia }}
              </span>
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group">
              <label>Valor <span class="required">*</span></label>
              <input
                v-model="valorInput"
                type="text"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.valor }"
                placeholder="0,00"
                @blur="formatarValor"
              />
              <span v-if="formSubmitted && errors.valor" class="error-message">
                {{ errors.valor }}
              </span>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-6">
            <div class="form-group">
              <label>Histórico</label>
              <input
                v-model="transferencia.historico"
                type="text"
                class="form-control"
                placeholder="Ex: Transferência do caixa para conta bancária"
              />
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label>Documento</label>
              <input
                v-model="transferencia.documento"
                type="text"
                class="form-control"
                placeholder="Ex: Comprovante / nº doc"
              />
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-9">
            <div class="form-group">
              <label>Observações</label>
              <textarea
                v-model="transferencia.observacoes"
                class="form-control"
                rows="3"
                placeholder="Observações adicionais"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/services/axios'
import BaseForm from '@/components/Layout/BaseForm.vue'

const router = useRouter()
const route = useRoute()

const transferencia = reactive({
  id: null,
  conta_origem_id: '',
  conta_destino_id: '',
  data_transferencia: '',
  valor: 0,
  historico: '',
  documento: '',
  observacoes: ''
})

const contas = ref([])
const valorInput = ref('')

const errors = reactive({})
const formSubmitted = ref(false)
const isSubmitting = ref(false)

const isFormValid = computed(() => {
  return !!(
    transferencia.conta_origem_id &&
    transferencia.conta_destino_id &&
    transferencia.data_transferencia &&
    Number(transferencia.valor) > 0 &&
    Number(transferencia.conta_origem_id) !== Number(transferencia.conta_destino_id)
  )
})

function voltar() {
  router.push('/financeiro/transferencias-contas')
}

function formatarValor() {
  const numericValue = parseFloat(
    String(valorInput.value || '')
      .replace(/\./g, '')
      .replace(',', '.')
  )

  transferencia.valor = isNaN(numericValue) ? 0 : numericValue

  valorInput.value = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(transferencia.valor)
}

function validarFormulario() {
  Object.keys(errors).forEach(k => delete errors[k])

  if (!transferencia.conta_origem_id) {
    errors.conta_origem_id = 'Informe a conta de origem.'
  }

  if (!transferencia.conta_destino_id) {
    errors.conta_destino_id = 'Informe a conta de destino.'
  }

  if (
    transferencia.conta_origem_id &&
    transferencia.conta_destino_id &&
    Number(transferencia.conta_origem_id) === Number(transferencia.conta_destino_id)
  ) {
    errors.conta_destino_id = 'A conta de destino deve ser diferente da conta de origem.'
  }

  if (!transferencia.data_transferencia) {
    errors.data_transferencia = 'Informe a data da transferência.'
  }

  if (!(Number(transferencia.valor) > 0)) {
    errors.valor = 'Informe um valor maior que zero.'
  }

  return Object.keys(errors).length === 0
}

async function carregarContas() {
  try {
    const response = await axios.get('/financeiro/contas-financeiras', {
      params: { page: 1, limit: 1000 }
    })
    contas.value = Array.isArray(response.data?.data) ? response.data.data : []
  } catch (error) {
    console.error('Erro ao carregar contas financeiras:', error)
  }
}

async function carregarTransferencia(id) {
  try {
    const { data } = await axios.get(`/financeiro/transferencias-contas/${id}`)

    transferencia.id = data.id
    transferencia.conta_origem_id = data.conta_origem_id
    transferencia.conta_destino_id = data.conta_destino_id
    transferencia.data_transferencia = String(data.data_transferencia || '').slice(0, 10)
    transferencia.valor = parseFloat(data.valor || 0)
    transferencia.historico = data.historico || ''
    transferencia.documento = data.documento || ''
    transferencia.observacoes = data.observacoes || ''

    valorInput.value = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(transferencia.valor)
  } catch (error) {
    console.error('Erro ao carregar transferência:', error)
  }
}

async function salvarTransferencia() {
  formatarValor()
  formSubmitted.value = true

  if (isSubmitting.value) return
  if (!validarFormulario()) return

  isSubmitting.value = true

  try {
    const url = transferencia.id
      ? `/financeiro/transferencias-contas/${transferencia.id}`
      : '/financeiro/transferencias-contas'

    const method = transferencia.id ? 'put' : 'post'

    await axios[method](url, {
      conta_origem_id: Number(transferencia.conta_origem_id),
      conta_destino_id: Number(transferencia.conta_destino_id),
      data_transferencia: transferencia.data_transferencia,
      valor: Number(transferencia.valor),
      historico: transferencia.historico,
      documento: transferencia.documento,
      observacoes: transferencia.observacoes
    })

    router.push('/financeiro/transferencias-contas')
  } catch (error) {
    console.error('Erro ao salvar transferência:', error)
    errors.backend = error.response?.data?.error || 'Erro ao salvar transferência.'
    alert(errors.backend)
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  await carregarContas()

  const id = route.params.id
  if (id) {
    await carregarTransferencia(id)
  } else {
    transferencia.data_transferencia = new Date().toISOString().slice(0, 10)
    valorInput.value = '0,00'
  }
})
</script>

<style scoped>
.form-container {
  margin: 20px;
}
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}
.required {
  color: red;
}
.spacer {
  margin-bottom: 20px;
}
.error-message {
  color: #dc3545;
  font-size: 0.85rem;
}
.error-input {
  border-color: #dc3545;
}
</style>